<script setup lang="ts">
import {
  ref,
  useCreators,
  watch,
  useRouter,
  useStorage,
  callOnce,
  computed,
  useMaps,
  useHeaderStyle,
  usePlaces,
} from "#imports";
import MPButton from "~/components/forms/MPButton.vue";
import MPSectionHeader from "~/components/layouts/MPSectionHeader.vue";
import MPSkewContainer from "~/components/layouts/MPSkewContainer.vue";
import MPAccordion from "~/components/widgets/MPAccordion.vue";
import MPCategoryCard from "~/components/widgets/MPCategoryCard.vue";
import MPImg from "~/components/widgets/MPImg.vue";
import MPTag from "~/components/widgets/MPTag.vue";
import MPPlaceSearch from "~/components/forms/MPPlaceSearch.vue";
import type { Place } from "~/stores/places";

const { formattedImageUrl } = useStorage();
const creatorStore = useCreators();
const placeStore = usePlaces();
const mapsStore = useMaps();
const router = useRouter();

useHeaderStyle("opaque");

const featuredRowToggle = ref<"destinations" | "creators">("destinations");

const featuredCreators = computed(() =>
  creatorStore.featuredCreators?.map((creator) => ({
    ...creator,
    map: mapsStore.getById(creator.mapIds[0]!).value!,
  })),
);

await callOnce(
  async () => {
    await placeStore.fetchTopPlaces({ limit: 5 });
    const creators = await creatorStore.fetchFeaturedCreators({ limit: 9 });
    const mapIds = creators.value.map((creator) => creator.mapIds[0]!);
    await mapsStore.fetchByIds(mapIds);
  },
  { mode: "navigation" },
);

const selectedDestination = ref<Place | null>(null);
watch(selectedDestination, () => {
  if (!selectedDestination.value) return;
  router.push(
    `/explore/?country=${selectedDestination.value.country}&region=${selectedDestination.value.region}&place=${selectedDestination.value.place}`,
  );
});

const faqItems = [
  {
    id: "1",
    header: "What is Mappr?",
    content:
      "Mappr is a way to support your favorite creators and have access to their knowledge of the world.",
  },
  {
    id: "2",
    header: "How does Mappr work?",
    content:
      "We share revenue from subscriptions with our creators, helping to cultivate a rich community of creators and Explorers.",
  },
  {
    id: "3",
    header: "How can I get involved?",
    content:
      "You can join mappr as a creator or simply create an account to start saving spots, building your atlas and more.",
  },
];
</script>

<template>
  <div class="home-page">
    <section class="fold">
      <h5 class="subtitle" v-motion-fade :delay="200">
        For Those Seeking Deeper Travel;
      </h5>
      <h1 class="typeface-title" v-motion-fade>
        Authentic Insights From Passionate Local Experts
      </h1>
      <div class="featured">
        <div class="search-container">
          <MPPlaceSearch
            name="search-destinations"
            v-model="selectedDestination"
            placeholder="Where would you like to explore?"
          />
          <i data-v-3bad32bc="" class="ri-expand-up-down-line"></i>
        </div>
        <ul class="featured-tabs">
          <li
            :class="{ active: featuredRowToggle === 'destinations' }"
            @click="featuredRowToggle = 'destinations'"
          >
            Featured Destinations
          </li>
          <li
            :class="{ active: featuredRowToggle === 'creators' }"
            @click="featuredRowToggle = 'creators'"
          >
            Featured Creators
          </li>
        </ul>
        <TransitionGroup name="transition-list">
          <ul
            class="featured-content"
            v-if="featuredRowToggle === 'destinations'"
          >
            <li
              v-for="place in placeStore.topPlaces?.slice(0, 4) ?? []"
              :key="place.id"
            >
              <NuxtLinkLocale
                :to="`/explore/?country=${place.country}&region=${place.region}&place=${place.place}`"
              >
                <div class="featured-content-item">
                  <MPImg
                    :src="place.cover_image_url!"
                    :width="50"
                    :height="50"
                    alt="Destination Image"
                  />
                  <p>
                    {{ place.place }}
                    <small>{{ place.regionCountry }}</small>
                  </p>
                </div>
              </NuxtLinkLocale>
            </li>
          </ul>
          <ul class="featured-content" v-if="featuredRowToggle === 'creators'">
            <li
              v-for="creator in featuredCreators!.slice(0, 4)"
              :key="creator.id"
            >
              <NuxtLinkLocale :to="`/@${creator.mappr_handle}`">
                <div class="featured-content-item">
                  <MPImg
                    :src="
                      creator.profile_image_url ??
                      '/media/images/placeholder-user.webp'
                    "
                    :width="50"
                    :height="50"
                    alt="Creator Image"
                  />
                  <p>
                    @{{ creator.mappr_handle }}
                    <small>{{ creator.display_name }}</small>
                  </p>
                </div>
              </NuxtLinkLocale>
            </li>
          </ul>
        </TransitionGroup>
      </div>
      <div class="mock-up-graphic">
        <MPImg class="mobile-mockup" src="/images/mobile-mockup.webp" />
        <div class="image-rows">
          <MPImg
            v-motion-slide-visible-left
            :duration="500"
            src="/images/image-rows-1.webp"
          />
          <MPImg
            v-motion-slide-visible-right
            :duration="500"
            src="/images/image-rows-2.webp"
          />
          <MPImg
            v-motion-slide-visible-left
            :duration="500"
            src="/images/image-rows-3.webp"
          />
        </div>
      </div>
    </section>
    <section class="for-creators">
      <MPSectionHeader
        subtitle="Creator enabled"
        title="Curated by Those Exploring the World."
        summary="Unlock insights from real creators, discover hidden gems and secret spots that go beyond the usual travel guides."
        class="header"
      />
      <div class="image-grid">
        <MotionGroup preset="fadeVisibleOnce" :duration="150">
          <NuxtLinkLocale
            v-for="creator in featuredCreators"
            :key="creator.id"
            :to="`/@${creator.mappr_handle}`"
          >
            <MPImg
              :src="
                creator.profile_image_url ??
                '/media/images/placeholder-user.webp'
              "
              :width="600"
              :height="600"
            />
            <h5>
              <strong v-motion-slide-visible-once-bottom :delay="100"
                >@{{ creator.mappr_handle }}'s</strong
              >
              <span
                class="typeface-regular"
                v-motion-slide-visible-once-bottom
                :delay="200"
                >{{ creator.map.title }}</span
              >
            </h5>
          </NuxtLinkLocale>
        </MotionGroup>
      </div>
    </section>
    <section class="for-explorers">
      <MPSectionHeader
        subtitle="For Explorers"
        title="Treasures off the beaten path"
        summary="Get trusted recommendations by following your favorite creators, from influencers to local experts, ensuring every spot is handpicked and high quality."
        :centerTitle="false"
      />
      <NuxtLinkLocale to="/maps" v-motion-fade-visible-once>
        <MPButton label="Start exploring" rounded />
      </NuxtLinkLocale>
      <div class="category-grid">
        <div class="category-background">
          <MPSkewContainer
            id="fill-skew"
            fill="var(--color-slate-100)"
            class="fill"
            v-motion-slide-visible-once-right
            :duration="1000"
          />
          <MPSkewContainer
            id="outline-skew"
            v-motion-fade
            stroke="var(--color-indigo-200)"
            class="outline"
          />
          <MPImg
            src="/media/images/sushi-dude.webp"
            v-motion-slide-visible-once-right
            :duration="1000"
            class="sushi-dude"
          />
        </div>
        <NuxtLinkLocale
          to="/explore?category=Beach,Natural+spa"
          class="category"
        >
          <MPCategoryCard
            :title="$t('home.categoryCards.1.title')"
            :description="$t('home.categoryCards.1.text')"
            imageCover="/media/videos/3-poster.webp"
            videoSrc="/media/videos/3.webm"
            copyrightHolder="Video by Peggy Anke: https://www.pexels.com/video/woman-laid-in-a-hammock-6628063/"
          />
        </NuxtLinkLocale>
        <NuxtLinkLocale to="/explore?category=Restaurant,Cafe" class="category">
          <MPCategoryCard
            :title="$t('home.categoryCards.2.title')"
            :description="$t('home.categoryCards.2.text')"
            imageCover="/media/videos/4-poster.webp"
            videoSrc="/media/videos/4.webm"
            copyrightHolder="Video by cottonbro studio: https://www.pexels.com/video/a-person-uses-a-whit-cabbage-vegetable-for-added-toppings-in-a-noodle-soup-bowl-dish-3298229/"
          />
        </NuxtLinkLocale>
        <NuxtLinkLocale
          to="/explore?category=Bar,Nightlife,Pub,Clubs"
          class="category"
        >
          <MPCategoryCard
            :title="$t('home.categoryCards.3.title')"
            :description="$t('home.categoryCards.3.text')"
            imageCover="/media/videos/5-poster.webp"
            videoSrc="/media/videos/5.webm"
            copyrightHolder="Video by Yaroslav Shuraev: https://www.pexels.com/video/friends-walking-outside-7645530/"
          />
        </NuxtLinkLocale>
        <NuxtLinkLocale to="/explore?category=Museum,Historic" class="category">
          <MPCategoryCard
            :title="$t('home.categoryCards.4.title')"
            :description="$t('home.categoryCards.4.text')"
            imageCover="/media/videos/6-poster.webp"
            videoSrc="/media/videos/6.webm"
            copyrightHolder="Video by Antoni Shkraba: https://www.pexels.com/video/back-view-of-a-man-and-a-woman-sitting-on-a-couch-while-having-a-conversation-7986422/"
          />
        </NuxtLinkLocale>
      </div>
    </section>
    <section
      class="studio"
      :style="`background-image: url('/media/images/callout-studio-background.webp')`"
    >
      <div>
        <MPSectionHeader
          subtitle="Join Mappr"
          title="Become a Creator"
          summary="Your knowledge of the world is one of your most valuable assets. Share your favourite spots and get paid by your followers."
          invert
        />
        <NuxtLinkLocale to="/creators" v-motion-slide-visible-once-bottom>
          <MPButton label="Find out how" rounded version="primary" />
        </NuxtLinkLocale>
        <div class="screenshot">
          <MPSkewContainer
            v-motion-slide-visible-once-right
            id="fill-skew"
            fill="var(--color-rose-400)"
            class="fill"
          />
          <MPImg
            v-motion-fade-visible-once
            :delay="200"
            src="/media/images/studio-screenshot.webp"
          />
          <div v-motion-fade-visible-once class="beta-tag">
            <MPTag version="success"> New </MPTag>
            Creator Studio - Public Beta
          </div>
        </div>
      </div>
    </section>
    <section class="destinations">
      <MPSectionHeader
        subtitle="Destinations"
        title="A new way to explore the world"
        summary="Get the best travel tips, hidden gems, and global insights from our worldwide community of creators and travel experts."
      />
      <div class="destinations-list">
        <div
          v-for="destination in placeStore.topPlaces"
          :key="destination.id"
          class="destinations-item"
          :style="`background-image: url(${formattedImageUrl(destination.cover_image_url!, 600, 400)})`"
        >
          <NuxtLinkLocale :to="`/destinations/${destination.id}`">
            <div class="destinations-item-content">
              <h5>{{ destination.place }}</h5>
              <small>{{ destination.regionCountry }}</small>
            </div>
          </NuxtLinkLocale>
        </div>
        <div class="destinations-item callout">
          <div class="destinations-item-content">
            <div>
              <h5>Creator curated spots to visit</h5>
              <p>
                Find things to do, Restaurants to love, Attractions to enjoy &
                Experiences to remember
              </p>
            </div>
            <NuxtLinkLocale :to="`/destinations`">
              <MPButton
                label="Explore Destinations"
                version="primary"
                rounded
              />
            </NuxtLinkLocale>
          </div>
        </div>
      </div>
    </section>
    <section class="faq">
      <MPSectionHeader
        subtitle="Frequently Asked Questions"
        title="Frequently Asked Questions"
        summary="Get recommendations on spots, Travel info, offers and more from our community of creators & travel experts."
      />
      <MPAccordion :items="faqItems" class="faq-list" />
    </section>
  </div>
</template>

<style lang="postcss" scoped>
@import "@/assets/css/breakpoints.pcss";
@import "@/assets/css/scrollbar.pcss";

.home-page {
  position: relative;
  z-index: 0;
  top: -4.75rem;
  max-width: 100vw;
  overflow-x: hidden;
  @mixin scrollbar-reset;
  @mixin screen sm {
    all: unset;
    top: 0;
    margin-top: 2rem;
    max-width: 100%;
    overflow-x: hidden;
    @mixin scrollbar-reset;
  }
}

@define-mixin section-gap {
  padding: 4rem 1rem;
  @mixin screen sm {
    padding: 7rem 2rem;
  }
  @mixin screen xl {
    padding: 8rem 1rem;
  }
}

.fold {
  text-align: center;
  padding: 3rem 0 2rem;
  @mixin screen md {
    padding: 10vh 0 6rem;
  }

  h1 {
    max-width: 60rem;
    margin: 0 auto;
    padding: 0 1rem;
    @mixin screen md {
      padding: 0;
    }
  }

  .subtitle {
    color: var(--color-rose-400);
    padding: 0 1rem;
    @mixin screen md {
      padding: 0;
    }
  }
}

.featured {
  max-width: fit-content;
  margin: 0 auto;
  padding: 0 1rem;
  @mixin screen md {
    padding: 0;
  }

  .search-container {
    width: 25rem;
    margin: 1.5rem auto 2rem;
    position: relative;

    @mixin screen md {
      margin: 3rem auto 2.5rem;
    }

    &:deep(input) {
      border-radius: 1rem;
      border: 1px solid var(--color-slate-100);
      background-color: var(--color-slate-100);
      padding: 0.75rem 1rem 0.85rem 1rem;
      box-shadow: var(--shadow-sm);
      color: var(--color-slate-500);
      font-size: 1.2rem;
    }

    > i {
      position: absolute;
      color: var(--color-slate-400);
      top: 1rem;
      right: 0.75rem;
    }
  }

  .featured-tabs {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    gap: 1rem;
    color: var(--color-slate-400);
    li {
      cursor: pointer;
    }
    li.active {
      font-weight: 600;
    }
  }
  .featured-content {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    gap: 0.75rem;
    overflow-x: scroll;
    max-width: 100vw;
    @mixin scrollbar-reset;
  }
  .featured-content-item {
    background-color: var(--color-slate-100);
    border-radius: 1rem;
    padding: 0.5rem 1rem 0.5rem 0.75rem;
    display: grid;
    grid-template-columns: 50px 1fr;
    text-align: left;
    align-items: center;
    gap: 0.5rem;
    box-shadow: var(--shadow-sm);
    p {
      margin: 0;
      font-weight: 600;
      white-space: nowrap;
      color: var(--color-slate-600);
      small {
        display: block;
      }
    }
    img {
      width: 50px;
      height: 50px;
      border-radius: 0.5rem;
      object-fit: cover;
    }
    &:hover {
      background-color: var(--color-slate-100);
    }
  }
}

.mock-up-graphic {
  margin-top: 8rem;
  position: relative;
  @mixin screen md {
    margin-top: 10rem;
  }
  img.mobile-mockup {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 200px;
    margin: 0 auto;
    border: 10px solid var(--color-slate-900);
    box-shadow: 0 0 1px 1px var(--color-gray-600);
    border-radius: 2rem;
    object-fit: cover;
    z-index: 1;
  }
  .image-rows {
    display: grid;
    gap: 0.75rem;
    margin: 0 auto;
    align-items: center;
    justify-content: center;
    max-width: 100vw;
    img {
      margin: 0 auto;
      height: 6rem;
      object-fit: cover;
    }
  }
}

.for-creators {
  margin: 3rem auto 0;
  @mixin section-gap;
  text-align: center;
  width: 98vw;
  max-width: var(--default-content-width);
  @mixin screen sm {
    width: 90vw;
  }
  .header {
    margin: 0 auto;
  }
  .image-grid {
    margin-top: 6rem;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: repeat(6, 1fr);
    gap: 1rem;
    min-height: 70rem;
    @mixin screen sm {
      grid-template-columns: 1fr 1fr;
    }
    @mixin screen md {
      grid-template-rows: repeat(6, 1fr);
      grid-template-columns: repeat(8, 1fr);
      min-height: 40rem;
    }
    a {
      position: relative;
      border-radius: 0 0 1rem 1rem;
      box-shadow: 0;
      transition: all 0.2s ease-in-out;
      img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        aspect-ratio: 1/1;
        border-radius: 1rem;
      }
      h5 {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        background: linear-gradient(
          0deg,
          rgba(0, 0, 0, 0.8) 45%,
          rgba(0, 0, 0, 0) 100%
        );
        color: var(--color-slate-50);
        border-radius: 0 0 1rem 1rem;
        text-align: left;
        padding: 2rem 1.5rem 1.5rem;
        margin: 0;
        span {
          display: block;
          margin-top: 0.5rem;
          max-width: 20rem;
          color: var(--color-slate-200);
        }
      }
    }
    a:hover {
      box-shadow: var(--shadow-lg);
      transform: translateY(-0.15rem);
      transition: all 0.2s ease-in-out;
    }
    a {
      height: 15rem;
    }
    @mixin screen md {
      a {
        height: auto;
      }
      a:nth-child(1) {
        grid-column: 1 / span 2;
        grid-row: 1 / span 2;
      }
      a:nth-child(2) {
        grid-column: 3 / span 2;
        grid-row: 1 / span 3;
      }
      a:nth-child(3) {
        grid-column: 1 / span 2;
        grid-row: 3 / span 2;
      }
      a:nth-child(4) {
        grid-column: 5 / span 2;
        grid-row: 1 / span 4;
      }
      a:nth-child(5) {
        grid-column: 7 / span 2;
        grid-row: 1 / span 3;
      }
      a:nth-child(6) {
        grid-column: 1 / span 2;
        grid-row: 5 / span 2;
      }
      a:nth-child(7) {
        grid-column: 3 / span 2;
        grid-row: 4 / span 3;
      }
      a:nth-child(8) {
        grid-column: 5 / span 2;
        grid-row: 5 / span 2;
      }
      a:nth-child(9) {
        grid-column: 7 / span 2;
        grid-row: 4 / span 3;
      }
    }
  }
}

.for-explorers {
  margin: 0 auto;
  @mixin section-gap;
  text-align: left;
  width: 98vw;
  max-width: 100vw;
  overflow-x: visible;
  max-width: var(--default-content-width);
  @mixin screen sm {
    width: 90vw;
  }
  .category-grid {
    position: relative;
    margin: 8rem 0 4rem;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: repeat(4, 1fr);
    gap: 1rem;
    @mixin screen xs {
      grid-template-columns: repeat(2, 1fr);
      grid-template-rows: repeat(2, 1fr);
    }
    @mixin screen lg {
      grid-template-columns: repeat(4, 1fr);
      grid-template-rows: 1fr;
    }
    a {
      box-shadow: 0;
      transition: all 0.2s ease-in-out;
      border-radius: 1rem;
      max-height: 14rem;
      @mixin screen xs {
        max-height: 18rem;
      }
    }
    a:hover {
      box-shadow: var(--shadow-lg);
      transform: translateY(-0.15rem);
      transition: all 0.2s ease-in-out;
    }
  }
  .category-background {
    position: absolute;
    top: -410px;
    right: -200px;
    @mixin screen sm {
      top: -410px;
      right: 0;
    }
  }
  .outline {
    position: absolute;
    top: 0;
    right: 0;
    width: 250px;
    right: 110px;
    top: 190px;
    @mixin screen sm {
      width: 450px;
      right: 0;
      top: 0;
    }
  }
  .fill {
    position: absolute;
    width: 300px;
    right: 40px;
    top: 140px;
    @mixin screen sm {
      top: -120px;
      right: -200px;
      width: 600px;
    }
  }
  .sushi-dude {
    position: relative;
    z-index: 0;
    width: 150px;
    right: 190px;
    top: 200px;
    @mixin screen sm {
      width: 260px;
      right: 2rem;
      top: 65px;
    }
  }
}

.studio {
  min-height: 40rem;
  max-height: 100vh;
  background-size: cover;
  background-position: center center;
  display: flex;
  position: relative;
  margin-bottom: 10rem;
  @mixin screen sm {
    min-height: 50rem;
  }
  > div {
    margin: 0 auto;
    width: 100%;
    height: 100%;
    max-width: var(--default-content-width);
    padding: 0 1rem;
    z-index: 1;
    padding: 5rem 1rem;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    @mixin screen sm {
      padding: 10rem 1rem;
    }
  }
  .screenshot {
    position: absolute;
    bottom: -5%;
    left: 2rem;
    width: 80vw;
    box-shadow: var(--shadow-lg);
    border-radius: 1rem;
    @mixin screen xs {
      bottom: -15%;
      left: 4rem;
      width: 80vw;
    }
    @mixin screen md {
      left: 50%;
      transform: translateX(-50%);
      max-width: 40rem;
    }
    img {
      border-radius: 1rem;
      object-fit: cover;
      width: 100%;
    }
    .beta-tag {
      position: absolute;
      top: -2rem;
      left: 0;
      color: var(--color-white);
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    .fill {
      position: absolute;
      top: -50px;
      right: -5rem;
      width: 150px;
      z-index: -1;
      @mixin screen xs {
        width: 250px;
      }
    }
    .outline {
      position: absolute;
      top: 50px;
      right: -20px;
      width: 100px;
      @mixin screen xs {
        width: 200px;
      }
    }
  }
}
.studio:after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    -90deg,
    rgba(0, 0, 0, 0.4) 0%,
    rgba(0, 0, 0, 1) 100%
  );
  backdrop-filter: blur(3px);
  border-radius: 1rem;
}

.destinations {
  margin: 0 auto;
  @mixin section-gap;
  display: grid;
  text-align: center;
  justify-items: center;
  width: 98vw;
  max-width: var(--default-content-width);
  @mixin screen sm {
    width: 90vw;
  }
}
.destinations-list {
  margin-top: 2rem;
  display: grid;
  width: 100%;
  grid-template-columns: 1fr;
  gap: 1rem;
  padding: 0 1rem;
  grid-auto-flow: dense;
  @mixin screen sm {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    > :nth-child(5),
    > :nth-child(3) {
      grid-column: span 2;
    }
  }
  @mixin screen md {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    > :nth-child(5),
    > :nth-child(3) {
      grid-column: span 2;
    }
  }
  @mixin screen lg {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    > :nth-child(5),
    > :nth-child(3) {
      grid-column: span 2;
    }
  }
}
.destinations-item {
  min-height: 12rem;
  border-radius: 1rem;
  background-position: center;
  background-size: cover;
  color: var(--color-slate-50);
  background-color: var(--color-slate-500);
  @mixin screen sm {
    height: 18rem;
  }
}
.destinations-item-content {
  text-align: left;
  border-radius: 1rem;
  display: flex;
  flex-direction: column;
  align-items: bottom;
  padding: 1rem;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    -20deg,
    rgba(0, 0, 0, 0) 30%,
    rgba(0, 0, 0, 0.7) 100%
  );
  h5 {
    margin: 0;
    font-weight: 600;
  }
  small {
    color: var(--color-slate-200);
  }
}

.callout {
  background-color: var(--color-slate-100);
  color: var(--color-slate-900);
  border: 1px solid var(--color-slate-200);
  .destinations-item-content {
    justify-content: space-between;
    background-image: none;
    padding: 1.5rem;
    h5 {
      margin: 0;
    }
    p {
      color: var(--color-slate-500);
    }
  }
}

.faq {
  margin: 0 auto;
  @mixin section-gap;
  max-width: var(--default-content-width);
  display: grid;
  text-align: center;
  justify-items: center;
}
.faq-list {
  max-width: 40rem;
  margin: 0 auto;
}
</style>
